import { useGetSession } from "@/api/auth/queries";
import { analytics } from "@/lib/segment-analytics";
import { useEffect } from "react";

/* Prop Types */
interface SegmentData {
  groupType: string;
  id: string;
  name: string;
  type: string;
  tier: string;
  createdAt: string;
  parentGroupId?: string;
}

/*
 * useSegmentIdentify hook
 * This hook is used to identify the user and group them in segment
 *
 * It gets called asap once the session is loaded
 */
export const useSegmentIdentify = () => {
  /* Queries */
  const { data: session, isLoading: isSessionLoading, isError: isSessionError } = useGetSession();

  /* Constants */
  const user = session?.user;

  /* Side effects */
  useEffect(() => {
    if (!isSessionLoading && !isSessionError && session && user) {
      /* Base segment data */
      const segmentData: SegmentData = {
        groupType: "account",
        id: session.account.id,
        name: session.account.company,
        type: session.account.type,
        tier: session.account.tier,
        createdAt: session.account.createdAt,
      };

      // set the parentGroupId if they have an organisation
      if (session?.account?.organisation?.id) {
        segmentData.parentGroupId = session.account.organisation.id;
      }

      // identify the user
      analytics.identify(user.id, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        name: `${user.firstName} ${user.lastName}`,
        createdAt: user.createdAt,
        id: user.id,
      });

      // group the user
      analytics.group(
        session?.id,
        {
          ...segmentData,
        },
        {
          groupId: session?.account.id,
          userId: user?.id,
        },
      );

      // group the organisation if they have one
      if (session?.account?.organisation?.id) {
        analytics.group(session.account.organisation.id, {
          groupType: "organization",
          id: session.account.organisation.id,
          name: session.account.organisation.name,
          createdAt: session.account.organisation.createdAt,
          updatedAt: session.account.organisation.updatedAt,
        });
      }
    }
  }, [isSessionLoading, isSessionError, session, user]);
};
